import { CurrentSchoolYear } from '../../../ng2/shared/constants/current-school-year.constant';
import { IWindowService } from 'angular';
import { IStateService } from 'angular-ui-router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Toggles } from '../../../ng2/shared/constants/toggles.constant';
import { USER_ROLE_PERMISSIONS_FOR_STATES } from '../../../ng2/shared/constants/user-role-permissions-for-states.constant';
import { ToggleService } from '../../../ng2/shared/services/toggle/toggle.service';
import { SpinnerModal } from '../../shared/modals/status-spinner/status-spinner.service';
import { LoadSchoolAssessments } from './../../../ng2/store/actions/assessments-actions/school-assessments-actions';
import { LoadStudentAssessments } from './../../../ng2/store/actions/assessments-actions/student-assessments-actions';
import {
  getSchoolAssessmentsEntitiesList,
  getSchoolAssessmentsLoadedStatus,
} from './../../../ng2/store/selectors/assessments-selectors/school-assessments-selectors';
import {
  getStudentAssessmentsEntitiesList,
  getStudentAssessmentsLoadedStatus,
} from './../../../ng2/store/selectors/assessments-selectors/student-assessments-selectors';

angular.module('nv.routes.main').config($stateProvider => {
  /* istanbul ignore next */
  $stateProvider
    .state('main.school.data-entry', {
      abstract: true,
      url: '/data-entry',
      template: '<div ui-view></div>',
      data: {},
      onExit: ($window: IWindowService) => {
        $window.document.title = 'Data Portal';
      },
    })

    .state('main.school.data-entry.assessments', {
      abstract: false,
      url: '/assessments',
      resolve: {
        currentRoute: () => {
          return 'Assessments';
        },
        studentAssessments$: [
          'Store',
          'currentSchool',
          (Store, currentSchool) => {
            const { _id } = currentSchool;
            const payload = { schoolId: _id, schoolYear: CurrentSchoolYear.WITH_SY_PREFIX };
            const studentAssessmentsLoaded = getStudentAssessmentsLoadedStatus;
            const destroy$: Subject<boolean> = new Subject<boolean>();

            return new Promise(resolve => {
              Store.select(studentAssessmentsLoaded)
                .pipe(takeUntil(destroy$))
                .subscribe(loaded => {
                  if (!loaded) {
                    Store.dispatch(new LoadStudentAssessments(payload));
                  } else {
                    const studentAssessments = getStudentAssessmentsEntitiesList;
                    const studentAssessments$ = Store.select(studentAssessments);
                    destroy$.next(true);
                    destroy$.unsubscribe();
                    resolve(studentAssessments$);
                  }
                });
            });
          },
        ],
        schoolAssessments$: [
          'Store',
          'currentSchool',
          (Store, currentSchool) => {
            const { _id } = currentSchool;
            const payload = { schoolId: _id, schoolYear: CurrentSchoolYear.WITH_SY_PREFIX };
            const schoolAssessmentsLoaded = getSchoolAssessmentsLoadedStatus;
            const destroy$: Subject<boolean> = new Subject<boolean>();

            return new Promise(resolve => {
              Store.select(schoolAssessmentsLoaded)
                .pipe(takeUntil(destroy$))
                .subscribe(loaded => {
                  if (!loaded) {
                    Store.dispatch(new LoadSchoolAssessments(payload));
                  } else {
                    const schoolAssessments = getSchoolAssessmentsEntitiesList;
                    const schoolAssessments$ = Store.select(schoolAssessments);
                    destroy$.next(true);
                    destroy$.unsubscribe();
                    resolve(schoolAssessments$);
                  }
                });
            });
          },
        ],
      },
      template: `
        <nv-data-entry
          [current-user]="$resolve.currentUser"
          [school-assessments$]="$resolve.schoolAssessments$"
          [student-assessments$]="$resolve.studentAssessments$"
          [current-route]="$resolve.currentRoute"
          [current-school]="$resolve.currentSchool"
        ></nv-data-entry>
      `,
      data: {
        roles: USER_ROLE_PERMISSIONS_FOR_STATES['main.school.data-entry.assessments'],
      },
      onEnter: (
        SpinnerModal: SpinnerModal,
        $state: IStateService,
        ToggleService: ToggleService,
        $window: angular.IWindowService,
      ) => {
        $state.transition.then(() => {
          const nyctToggleIsOn = ToggleService.getToggleState(Toggles.TOGGLE_NYCT_MVP);
          if (!nyctToggleIsOn) $state.go('main.home');
        });
        SpinnerModal.deactivateSpinner();
        $window.document.title = 'Data Entry - Assessments | Data Portal';
      },
    })
    .state('main.school.data-entry.admin-input', {
      abstract: false,
      url: '/assessment/:schoolAssessmentId',
      resolve: {
        studentAssessments$: [
          'Store',
          'currentSchool',
          (Store, currentSchool) => {
            const { _id } = currentSchool;
            const payload = { schoolId: _id, schoolYear: CurrentSchoolYear.WITH_SY_PREFIX };
            const studentAssessmentsLoaded = getStudentAssessmentsLoadedStatus;
            const destroy$: Subject<boolean> = new Subject<boolean>();

            return new Promise(resolve => {
              Store.select(studentAssessmentsLoaded)
                .pipe(takeUntil(destroy$))
                .subscribe(loaded => {
                  if (!loaded) {
                    Store.dispatch(new LoadStudentAssessments(payload));
                  } else {
                    const studentAssessments = getStudentAssessmentsEntitiesList;
                    const studentAssessments$ = Store.select(studentAssessments);
                    destroy$.next(true);
                    destroy$.unsubscribe();
                    resolve(studentAssessments$);
                  }
                });
            });
          },
        ],
        schoolAssessments$: [
          'Store',
          'currentSchool',
          (Store, currentSchool) => {
            const { _id } = currentSchool;
            const payload = { schoolId: _id, schoolYear: CurrentSchoolYear.WITH_SY_PREFIX };
            const schoolAssessmentsLoaded = getSchoolAssessmentsLoadedStatus;
            const destroy$: Subject<boolean> = new Subject<boolean>();

            return new Promise(resolve => {
              Store.select(schoolAssessmentsLoaded)
                .pipe(takeUntil(destroy$))
                .subscribe(loaded => {
                  if (!loaded) {
                    Store.dispatch(new LoadSchoolAssessments(payload));
                  } else {
                    const schoolAssessments = getSchoolAssessmentsEntitiesList;
                    const schoolAssessments$ = Store.select(schoolAssessments);
                    destroy$.next(true);
                    destroy$.unsubscribe();
                    resolve(schoolAssessments$);
                  }
                });
            });
          },
        ],
      },
      template: `
        <assessments-data-input
          [school-assessments$]="$resolve.schoolAssessments$"
          [student-assessments$]="$resolve.studentAssessments$"
        ></assessments-data-input>
      `,
      data: {
        roles: USER_ROLE_PERMISSIONS_FOR_STATES['main.school.data-entry.admin-input'],
      },
      params: {
        schoolAssessmentId: null,
      },
      onEnter: (
        SpinnerModal: SpinnerModal,
        $state: IStateService,
        ToggleService: ToggleService,
        $window: angular.IWindowService,
      ) => {
        $state.transition.then(() => {
          const nyctToggleIsOn = ToggleService.getToggleState(Toggles.TOGGLE_NYCT_MVP);
          if (!nyctToggleIsOn) $state.go('main.home');
        });
        SpinnerModal.deactivateSpinner();
        $window.document.title = 'Data Entry - Assessments | Data Portal';
      },
    });
});
